import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
    ApplicationFacade,
    FavoritesFacade,
    JourneyDirection,
    MapCoordinates,
    MapIcons,
    OpenTripPlannerFacade,
    RealtimeFacade,
    StopMarker
} from '@ridango/components';
import { LeafletApiService, MapApiService } from '../../map/services';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent, LatLng as RidLatLng, WebSocketService } from '@ridango/core';
import { Icon, LatLng, Marker } from 'leaflet';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';
import Env from '@ridango/core/env';
import { IconSizeEnum, IconUtils, MarkerType, IconSizeOrderEnum } from '../../map/services/map-icon.utils';
import { combineLatest } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const SizeValuesMap = {
    [MarkerType.stop]: IconUtils.getStopIconSizeValues,
    [MarkerType.stopFavorite]: IconUtils.getFavoriteStopIconSizeValues,
    [MarkerType.bus]: IconUtils.getBusIconSizeValues
};

@Component({
    selector: 'rwidget-select-from-map-modal',
    templateUrl: './select-from-map-modal.component.html',
    styleUrls: ['./select-from-map-modal.component.scss'],
    providers: [{ provide: MapApiService, useClass: LeafletApiService }]
})
export class SelectFromMapModalComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() modalType: JourneyDirection;
    assetsPath$ = this.applicationFacade.assetsPath$;
    busLocations$ = this.realtime.locations$;
    JourneyDirection = JourneyDirection;
    mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    markerIcon: Marker;
    iconOrigin = MapIcons('journeyPlannerOrigin', this.env.APP_NAME);
    clickedStop: StopMarker | null = null;
    stops$ = this.openTripPlannerFacade.stops$;
    iconStop = MapIcons('stop', this.env.THEME_NAME);
    isShowPopup = this.env.JOURNEY_PLANNER?.SHOW_STOP_POPUP_ON_MAP;
    locationButtonColor: string;
    lastCheckedLocation: RidLatLng = null;
    allFormattedStops: StopMarker[] = [];
    allFavoriteStops: StopMarker[] = [];
    favoriteStops$ = this.favoritesFacade.favoriteStops$;

    formattedStops = [];
    favoriteStops: StopMarker[] = [];
    zoomIconSizeValue: IconSizeEnum;
    zoomValue: number;
    isContrast$ = this.applicationFacade.highContrast$;

    readonly MarkerType = MarkerType;
    readonly IconSizeEnum = IconSizeEnum;
    readonly IconSizeOrderEnum = IconSizeOrderEnum;
    readonly stopsVisibleFromZoomSize: IconSizeEnum =
        this.env.MAP.STOPS_VISIBLE_FROM_ZOOM_SIZE ?? this.IconSizeEnum.small;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private mapApi: MapApiService,
        private cdr: ChangeDetectorRef,
        private realtime: RealtimeFacade,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private webSocket: WebSocketService,
        private zone: NgZone,
        private favoritesFacade: FavoritesFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.setLocationButtonColor();
        combineLatest(this.stops$, this.favoriteStops$)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([stops, favoriteStops]) => {
                this.allFormattedStops = stops.map((s) => ({
                    ...s,
                    latlng: { lat: s.lat, lng: s.lon }
                }));
                this.allFavoriteStops = this.allFormattedStops.filter(
                    (s) =>
                        !this.env.JOURNEY_PLANNER?.HIDE_FAVORITE_FROM_MAP &&
                        !!favoriteStops.find((fs) => fs.stopId === s.id)
                );
                this.setStopMarkers();
            });

        this.openTripPlannerFacade.location$.pipe(takeUntil(this.componentDestroyed$)).subscribe((location) => {
            if (!location?.coordinates || location.hasError) {
                return;
            }

            this.lastCheckedLocation = location.coordinates;
        });

        if (this.env.REALTIME_VEHICLE_STATUS_PATH) {
            this.subscribeRealtimeBusLocations();
        }
    }

    ngAfterViewInit() {
        if (this.isShowPopup) {
            this.setMarkerOnMap();
        }
    }

    onZoomChanged(zoom) {
        this.zoomValue = zoom;
        this.zoomIconSizeValue = IconUtils.getIconSize(zoom);
    }

    setMarkerOnMap() {
        const icon = new Icon({ iconUrl: 'assets/img/journey-planner/destination.svg' });
        this.markerIcon = new Marker([this.mapCenter.latitude, this.mapCenter.longitude], {
            icon,
            zIndexOffset: 1000
        });
        this.mapApi.addToMap(this.markerIcon);

        this.zone.runOutsideAngular(() => {
            this.mapApi.addListener('move', () => {
                this.markerIcon.setLatLng(this.mapApi.getCenterCoordinates());
            });
        });
    }

    setStopMarkers() {
        if (this.env.JOURNEY_PLANNER?.HIDE_FAVORITE_FROM_MAP) {
            this.formattedStops = this.allFormattedStops;
        } else {
            this.formattedStops = this.allFormattedStops.filter(
                (s) => !this.allFavoriteStops.find((fs) => fs.id === s.id)
            );
            this.favoriteStops = this.allFormattedStops.filter((s) =>
                this.allFavoriteStops.find((fs) => fs.id === s.id)
            );
        }
    }

    getIconSize(type: MarkerType): number {
        if (!this.mapApi.getNativeMap()) {
            return;
        }

        return SizeValuesMap[type] ? SizeValuesMap[type](this.zoomValue) : null;
    }

    onRouteStopClick(stop: StopMarker) {
        if (!this.isShowPopup) {
            return;
        }
        this.clickedStop = stop;
        this.cdr.detectChanges();
    }

    clickOnBusStopInfo() {
        const positionBus = new LatLng(this.clickedStop.latlng.lat, this.clickedStop.latlng.lng);
        const name = this.clickedStop?.name;
        const desc = this.clickedStop?.desc;
        this.clickedStop = null;
        return this.modalCtrl.dismiss({ positionBus, name, desc });
    }

    cancel() {
        return this.modalCtrl.dismiss();
    }

    confirm() {
        const coordinates = this.isShowPopup ? this.markerIcon.getLatLng() : this.mapApi.getCenterCoordinates();

        return this.modalCtrl.dismiss({
            positionBus: coordinates,
            name: 'LOCATION_FROM_MAP',
            desc: null
        });
    }

    setLocationButtonColor() {
        switch (this.env.THEME_NAME) {
            case ThemeFeature.GOTLAND_V2:
                this.locationButtonColor = 'secondary';
                break;
            case ThemeFeature.VASTMANLAND:
                this.locationButtonColor = 'theme_green';
                break;
            case ThemeFeature.VARMLAND:
                this.locationButtonColor = 'primary';
                break;
            default:
                this.locationButtonColor = 'secondary';
        }
    }

    private subscribeRealtimeBusLocations(): void {
        this.zone.runOutsideAngular(() => {
            this.webSocket
                .connect(this.env.REALTIME_VEHICLE_STATUS_PATH)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((buses) => {
                    this.realtime.setLocations(buses);
                });
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
